import { StackLayout } from '@euler/app/components/StackLayout';
import { InspectionIcon } from '@euler/app/components/icons/InspectionIcon';
import { SvgIconProps } from '@euler/app/components/icons/types';
import { useSvgProps } from '@euler/app/components/icons/util';
import { Typography } from '@euler/app/components/typography';
import {
  FaultInspectionItemNote,
  FaultInspectionItemStatusBadge,
} from '@euler/app/flows/task/pages/detail/components/FaultInspectionInfo';
import { maintenancePlanListItemInfoFromItem } from '@euler/app/flows/task/pages/finish-task/utils';
import { Center } from '@euler/components/Center';
import { StatusColors } from '@euler/components/Colors';
import { Icon } from '@euler/components/Icomoon';
import { Img } from '@euler/components/image/AdvancedImage';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { FaultInspectionItem } from '@euler/model/FaultInspectionItem';
import { ShieldCheck } from 'lucide-react-native';
import { FC, memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { Animated, StyleProp, Text, ViewStyle } from 'react-native';
import Svg, { G, Path } from 'react-native-svg';
import { useTheme } from 'styled-components';
import { MaintenancePlanListItem } from '../types';
import {
  CustomerSignInfo,
  MaintenancePlanInfoView,
} from './MaintenancePlanInfoView';

export const MaintenancePlanItemCell = memo(
  ({
    item,
    style,
    error,
    errorShakeNonce,
    onPressAction,
    onViewDetail,
    onEditPlanInfo,
    onEditNote,
    onViewFaultRelatedInspectionDetails,
    onViewFaultRelatedJobDetails,
  }: {
    item: MaintenancePlanListItem;
    style?: StyleProp<ViewStyle>;
    error?: string;
    errorShakeNonce?: number;
    onPressAction: (item: MaintenancePlanListItem) => void;
    onViewDetail?: (item: MaintenancePlanListItem) => void;
    onEditPlanInfo?: (item: MaintenancePlanListItem) => void;
    onEditNote?: (item: MaintenancePlanListItem) => void;
    onViewFaultRelatedInspectionDetails?: (item: FaultInspectionItem) => void;
    onViewFaultRelatedJobDetails?: (item: FaultInspectionItem) => void;
  }) => {
    const { rpx } = useResponsiveContext();

    const theme = useTheme();
    const itemInfo = useMemo(
      () => maintenancePlanListItemInfoFromItem(item),
      [item],
    );

    const action = itemInfo.action;
    const actionDisabled =
      (item.type === 'custom' && item.removable === false) ||
      (item.type === 'insurance-expiry' &&
        item.removable === false &&
        item.optedIn);

    const handleActionPress = useCallback(() => {
      onPressAction(item);
    }, [item, onPressAction]);

    const handleEditPlan = useCallback(() => {
      onEditPlanInfo?.(item);
    }, [item, onEditPlanInfo]);

    const handleEditNote = useCallback(() => {
      onEditNote?.(item);
    }, [item, onEditNote]);

    const handleViewDetail = useCallback(() => {
      onViewDetail?.(item);
    }, [item, onViewDetail]);

    const shakeX = useRef(new Animated.Value(0)).current;
    useEffect(() => {
      if (!error || !errorShakeNonce) return;
      Animated.sequence([
        Animated.timing(shakeX, {
          toValue: -6,
          duration: 50,
          useNativeDriver: true,
        }),
        Animated.timing(shakeX, {
          toValue: 6,
          duration: 50,
          useNativeDriver: true,
        }),
        Animated.timing(shakeX, {
          toValue: -4,
          duration: 50,
          useNativeDriver: true,
        }),
        Animated.timing(shakeX, {
          toValue: 4,
          duration: 50,
          useNativeDriver: true,
        }),
        Animated.timing(shakeX, {
          toValue: 0,
          duration: 50,
          useNativeDriver: true,
        }),
      ]).start();
    }, [errorShakeNonce, error, shakeX]);

    return (
      <StackLayout style={style} gap={rpx(4)}>
        <StackLayout
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={rpx(10)}
        >
          <ItemIcon item={item} />
          <StackLayout flex={1} gap={rpx(4)} style={{ marginRight: rpx(5) }}>
            <StackLayout direction="row" alignItems="center" spacing={rpx(6)}>
              <Typography.Text medium size={rpx(16)} color="#333">
                {itemInfo.name}
              </Typography.Text>
              {item.type === 'pending-issue' ? (
                <Center.Touchable onPress={handleViewDetail}>
                  <Icon
                    name="information-outline"
                    size={rpx(14)}
                    color="#aaa"
                  />
                </Center.Touchable>
              ) : item.type === 'fault' ? (
                <FaultInspectionItemStatusBadge
                  item={item.faultInspectionItem}
                />
              ) : null}
            </StackLayout>
            {itemInfo.subTitle &&
            item.type !== 'custom' &&
            (item.type !== 'pending-issue' || item.repairConfirmed) ? (
              <Typography.Text size={rpx(12)} color="#555" mb={rpx(3)}>
                {itemInfo.subTitle}
              </Typography.Text>
            ) : null}
            <MaintenancePlanInfoView
              nextServiceDate={item?.planInfo?.nextServiceDate}
              nextServiceMileage={item?.planInfo?.nextServiceMileage}
              estimatedNextServiceMileage={
                item?.planInfo?.estimatedNextServiceMileage
              }
              remark={item?.planInfo?.remark}
              variant={
                item.type === 'insurance-expiry' ? 'insurance' : 'default'
              }
              label={item.type === 'custom' ? item.label : undefined}
              onEdit={handleEditPlan}
            />
            {((item.type === 'pending-issue' && !item.repairConfirmed) ||
              (item.type === 'fault' && !item.solved)) &&
            item.unsolvedReason ? (
              <StackLayout style={{ marginTop: rpx(4) }} spacing={rpx(4)}>
                <StackLayout direction="row" gap={rpx(5)} alignItems="center">
                  <Typography.Text size={rpx(12)} color="#555">
                    {'遗留原因: '}
                    <Text style={{ color: theme.colors.status.warn }}>
                      {item.unsolvedReason}
                    </Text>
                  </Typography.Text>
                  <Center.Touchable>
                    <Icon
                      name="note"
                      size={rpx(12)}
                      color={theme.link}
                      onPress={handleEditNote}
                    />
                  </Center.Touchable>
                </StackLayout>

                {item.remark?.trim() ? (
                  <Typography.Text size={rpx(12)} color="#555">
                    {'遗留备注: '}
                    <Text style={{ color: '#777' }}>{item.remark}</Text>
                  </Typography.Text>
                ) : null}
              </StackLayout>
            ) : null}
            {item.type === 'pending-issue' && !item.repairConfirmed ? (
              <CustomerSignInfo
                imgUrl={item.customerSignatureImgUrl}
                unavailableReason={item.customerSignatureUnavailableReason}
                unavailableRemark={item.customerSignatureUnavailableRemark}
              />
            ) : null}
            {item.type === 'fault' && (
              <FaultInspectionItemNote
                item={item.faultInspectionItem}
                withAuthors={false}
                onViewInspectionDetails={onViewFaultRelatedInspectionDetails}
                onViewJobDetails={onViewFaultRelatedJobDetails}
              />
            )}
          </StackLayout>
          <Center
            css={`
              margin-right: ${rpx(action.type === 'button' ? 0 : -4)}px;
            `}
          >
            <Center.Touchable
              onPress={actionDisabled ? undefined : handleActionPress}
              disabled={actionDisabled}
              css={`
                background-color: ${actionDisabled
                  ? '#f0f0f0'
                  : action.backgroundColor ?? 'transparent'};
                border: ${action.borderColor ? 1 : 0}px solid
                  ${actionDisabled
                    ? '#e0e0e0'
                    : action.borderColor ?? 'transparent'};
                padding: 0 ${rpx(action.type === 'button' ? 12 : 0)}px;
                border-radius: ${rpx(action.type === 'button' ? 20 : 0)}px;
                height: ${rpx(
                  ((item.type === 'fault' && !item.solved) ||
                    (item.type === 'pending-issue' && !item.repairConfirmed)) &&
                    !item.unsolvedReason
                    ? 24
                    : 32,
                )}px;
                flex-direction: row;
                align-items: center;
                opacity: ${actionDisabled ? 0.6 : 1};
              `}
            >
              {action.icon ? (
                <Icon
                  name={action.icon}
                  size={rpx(action.iconSize!)}
                  color={actionDisabled ? '#aaa' : action.iconColor}
                  style={{ marginRight: rpx(3) }}
                />
              ) : null}
              <Typography.Text
                size={rpx(13)}
                medium
                lineHeight={rpx(20)}
                color={actionDisabled ? '#aaa' : action.textColor}
              >
                {action.title}
              </Typography.Text>
              {action.actionType === 'view-detail' && (
                <Icon
                  name="chevron-right"
                  size={rpx(20)}
                  color={actionDisabled ? '#aaa' : action.textColor}
                />
              )}
            </Center.Touchable>
            {((item.type === 'fault' && !item.solved) ||
              (item.type === 'pending-issue' && !item.repairConfirmed)) &&
            !item.unsolvedReason ? (
              <Center.Touchable
                onPress={handleEditNote}
                style={{ marginTop: rpx(4) }}
              >
                <Typography.Text
                  size={rpx(12)}
                  color={'#777'}
                  medium
                  letterSpacing={rpx(-0.35)}
                >
                  + {item.type === 'fault' ? '未解决' : '遗留'}原因
                </Typography.Text>
              </Center.Touchable>
            ) : null}
          </Center>
        </StackLayout>
        {error ? (
          <Animated.View
            style={{
              marginLeft: rpx(42),
              transform: [{ translateX: shakeX }],
            }}
          >
            <StackLayout direction="row" alignItems="center" gap={rpx(4)}>
              <Icon
                name="alert-octagon"
                size={rpx(14)}
                color={StatusColors.Danger}
                style={{ top: rpx(0.5) }}
              />
              <Typography.Text size={rpx(12)} color={StatusColors.Danger}>
                {error}
              </Typography.Text>
            </StackLayout>
          </Animated.View>
        ) : null}
      </StackLayout>
    );
  },
);

const ItemIcon = memo(
  ({
    item,
    style,
  }: {
    item: MaintenancePlanListItem;
    style?: StyleProp<ViewStyle>;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <Center
        style={style}
        css={`
          width: ${rpx(32)}px;
          height: ${rpx(32)}px;
        `}
      >
        {item.type === 'inspection-subject' && item.iconUrl ? (
          <Img uri={item.iconUrl} width={rpx(32)} height={rpx(32)} />
        ) : item.type === 'inspection-subject' ? (
          <InspectionIcon size={rpx(32)} />
        ) : item.type === 'construction-job' ||
          item.type === 'pending-issue' ? (
          <JobItemIcon size={rpx(30)} />
        ) : item.type === 'fault' ? (
          <Icon name="warning" color={StatusColors.Notice} size={rpx(30)} />
        ) : item.type === 'insurance-expiry' ? (
          <ShieldCheck color={StatusColors.Info} size={rpx(30)} />
        ) : (
          <Icon
            name="notifications-outline"
            color={StatusColors.Notice}
            size={rpx(30)}
          />
        )}
      </Center>
    );
  },
);

export const JobItemIcon: FC<SvgIconProps> = memo(props => {
  const { fill = '#3C98FB', ...svgProps } = useSvgProps(props, 30, 30);
  return (
    <Svg viewBox="0 0 30 30" fill="none" {...svgProps}>
      <Path
        d="M29.6 17.9C29.4 18.58 29.13 19.24 28.81 19.89C28.72 20.08 28.62 20.25 28.51 20.44C28.31 20.8 28.07 21.15 27.82 21.49C27.53 21.88 27.21 22.25 26.84 22.6L26.33 23.13L24.99 24.47L22.62 26.84C22.29 27.17 21.96 27.45 21.62 27.73C21.04 28.18 20.43 28.55 19.79 28.87C16.78 30.38 13.19 30.37 10.18 28.85C9.55 28.55 8.94 28.17 8.38 27.73C8.03 27.45 7.69 27.17 7.36 26.84L4.99 24.47L3.66 23.13L3.15 22.62C2.33 21.79 1.65 20.87 1.16 19.88C1.1 19.79 1.06 19.69 1.01 19.59C0.76 19.04 0.55 18.48 0.4 17.92C0.22 17.3 0.11 16.69 0.05 16.07C-0.26 12.97 0.77 9.74 3.15 7.38L7.38 3.15C11.58 -1.06 18.42 -1.06 22.62 3.15L26.85 7.37C29.7 10.22 30.61 14.28 29.6 17.9Z"
        fill={fill}
        fillOpacity="1.000000"
        fillRule="evenodd"
      />
      <G opacity="0.150000">
        <Path
          d="M28.84 19.74C28.75 19.93 28.65 20.11 28.54 20.3C28.34 20.67 28.1 21.02 27.85 21.37C27.56 21.76 27.24 22.14 26.87 22.49L26.36 23.03L25.02 24.38L22.64 26.8C22.32 27.12 21.98 27.42 21.64 27.7C21.06 28.15 20.45 28.53 19.81 28.85C16.79 30.39 13.2 30.37 10.18 28.84C9.55 28.53 8.94 28.14 8.38 27.7C8.02 27.42 7.69 27.13 7.36 26.8L4.98 24.38L3.65 23.03L3.14 22.51C2.31 21.67 1.64 20.73 1.15 19.73C2.7 14.36 7.2 10.29 12.75 9.42C13.47 9.29 14.24 9.23 15 9.23C15.77 9.23 16.53 9.29 17.27 9.42C22.8 10.29 27.31 14.38 28.84 19.74Z"
          fill="#FFFFFF"
          fillOpacity="1.000000"
          fillRule="evenodd"
        />
      </G>
      <G opacity="0.200000">
        <Path
          d="M25.38 23.91C25.38 24.08 25.38 24.27 25.35 24.45L22.89 26.83C22.56 27.16 22.21 27.44 21.86 27.72C21.26 28.17 20.63 28.55 19.97 28.86C16.85 30.38 13.13 30.37 10.01 28.85C9.36 28.55 8.73 28.16 8.15 27.72C7.78 27.44 7.43 27.16 7.1 26.83L4.64 24.45C4.61 24.27 4.61 24.09 4.61 23.91C4.61 20.48 6.38 17.44 9.09 15.64C10.42 14.73 11.99 14.13 13.7 13.92C14.13 13.87 14.55 13.84 15 13.84C15.44 13.84 15.88 13.87 16.3 13.92C17.97 14.12 19.55 14.72 20.93 15.65C23.62 17.47 25.38 20.48 25.38 23.91Z"
          fill="#FFFFFF"
          fillOpacity="1.000000"
          fillRule="evenodd"
        />
      </G>
      <G opacity="0.250000">
        <Path
          d="M21.92 24.05C21.92 25.94 21.12 27.67 19.81 28.89C16.79 30.37 13.19 30.36 10.17 28.88C8.87 27.65 8.07 25.94 8.07 24.05C8.07 23.59 8.12 23.16 8.2 22.74C8.8 19.79 11.37 17.52 14.53 17.33C14.68 17.3 14.83 17.3 15 17.3C15.16 17.3 15.32 17.3 15.47 17.33C18.62 17.54 21.19 19.79 21.79 22.74C21.87 23.16 21.92 23.59 21.92 24.05Z"
          fill="#FFFFFF"
          fillOpacity="1.000000"
          fillRule="evenodd"
        />
      </G>
      <Path
        d="M24.03 14.26L20.15 7.65C20.02 7.43 19.84 7.24 19.61 7.11C19.38 6.99 19.13 6.92 18.87 6.92L11.12 6.92C10.59 6.92 10.1 7.2 9.84 7.65L5.96 14.26C5.83 14.49 5.76 14.74 5.76 15C5.76 15.25 5.83 15.5 5.96 15.73L9.84 22.34C9.97 22.56 10.15 22.75 10.38 22.88C10.61 23 10.86 23.07 11.12 23.07L18.87 23.07C19.4 23.07 19.89 22.79 20.15 22.34L24.03 15.73C24.29 15.27 24.29 14.72 24.03 14.26ZM18.82 18.76C18.38 19.2 17.66 19.2 17.22 18.76L15.13 16.71C14.76 16.87 14.35 16.96 13.91 16.96C12.22 16.96 10.84 15.6 10.84 13.93C10.84 13.5 10.93 13.09 11.09 12.73L12.96 14.57L14.56 12.99L12.69 11.15C13.07 10.99 13.48 10.9 13.91 10.9C15.61 10.9 16.99 12.26 16.99 13.93C16.99 14.36 16.89 14.76 16.73 15.13L18.82 17.19C19.26 17.62 19.26 18.33 18.82 18.76Z"
        fill="#FFFFFF"
        fillOpacity="1.000000"
        fillRule="evenodd"
      />
    </Svg>
  );
});
